import {
    Button,
    Checkbox,
    createListCollection,
    Field,
    FileUpload,
    Icon,
    Input,
    Portal,
    Select,
    Text,
} from "@chakra-ui/react"
import { Controller } from "react-hook-form"
import FileDropzone from "../Common/FileDropzone"
import { FiUpload } from "react-icons/fi"
import { useEffect } from "react"
import { SettingsNavPointProps } from "../../api/navigation"


function SettingsNavPoint(props: SettingsNavPointProps) {
    const {
        order_number,
        parent_order_number,
        me_create,
        me_read,
        ct_read,
        categories_list,
        children,
        form,
    } = props

    const {
        register,
        setValue,
        getValues,
        control,
        formState: {errors},
    } = form

    // Create category collection from all categories
    const categoryCollection = createListCollection({
        items: categories_list?.data?.map(c => ({
            value: c.id,
            label: c.name,
        }))|| [],
    })

    // custom view collection
    const customViewCollection = createListCollection({
        items: [
            {value: "all-same-size", label: "Vierer-Layout (erste 4 Beiträge mit Bild)"},
            {value: "first-big-no-img", label: "Hauptbeitrag gross ohne Bilder (erster Beitrag gross, alle ohne Bilder)"},
            {value: "first-big-all-img", label: "Hauptbeitrag gross mit Bildern (erster Beitrag gross, alle mit Bilder)"},
            {value: "vertical-no-img", label: "Vertikales-Layout (keine Bilder)"},
            {value: "brissago", label: "Brissago"},
            {value: "gallery", label: "Galerie"},
            {value: "wz-tv", label: "WZ-TV"},
        ]
    })

    useEffect(() => {
        setValue(`navs.${order_number}.order_number`, order_number + 1, { shouldDirty: false })
        if((parent_order_number ?? 0) > 0) {
            setValue(`navs.${order_number}.parent_order_number`, ((parent_order_number ?? 0) + 1), { shouldDirty: false })
        }
    }, [order_number, parent_order_number])


    return (
        <>
            {/* Is active */}
            <Controller
                control={control}
                name={`navs.${order_number}.is_active`}
                defaultValue={true}
                render={({ field }) => (
                <Field.Root>
                    <Checkbox.Root
                        cursor="pointer"
                        checked={field.value}
                        onCheckedChange={({ checked }) => field.onChange(checked)}
                    >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>Aktiv</Checkbox.Label>
                    </Checkbox.Root>
                </Field.Root>
            )} />

            {/* Title */}
            <Field.Root mt={4} invalid={!!errors?.navs?.[order_number]?.title} required>
                <Field.Label htmlFor="title">
                    Titel
                    <Field.RequiredIndicator />
                </Field.Label>

                <Input
                    id="title"
                    {...register(`navs.${order_number}.title`, {
                        required: "Titel ist erforderlich",
                        maxLength: {
                            value: 255,
                            message: "Titel darf maximal 255 Zeichen lang sein",
                        },
                        minLength: 1
                        })
                    }
                    type="text"
                    placeholder="Titel"
                />

                {errors?.navs?.[order_number]?.title && (
                    <Field.ErrorText>{errors.navs?.[order_number]?.title.message}</Field.ErrorText>
                )}
            </Field.Root>

            {/* Path */}
            <Field.Root mt={4} invalid={!!errors?.navs?.[order_number]?.path} required>
                <Field.Label htmlFor="path">
                    Pfad
                    <Field.RequiredIndicator />
                </Field.Label>

                <Input
                    id="path"
                    {...register(`navs.${order_number}.path`, {
                            required: "Pfad ist erforderlich",
                            maxLength: {
                                value: 255,
                                message: "Pfad darf maximal 255 Zeichen lang sein",
                            },
                            minLength: 1
                        })
                    }
                    type="text"
                    placeholder="Pfad"
                />
                {errors?.navs?.[order_number]?.path && (
                    <Field.ErrorText>{errors?.navs?.[order_number]?.path.message}</Field.ErrorText>
                )}
            </Field.Root>

            {/* Category */}
            <Field.Root mt={4} required>
                <Field.Label htmlFor="fk_category_id">
                    Kategorie
                    <Field.RequiredIndicator />
                </Field.Label>

                <Controller
                    control={control}
                    name={`navs.${order_number}.fk_category_id`}
                    render={({ field }) => (
                        <Select.Root
                            multiple={false}
                            collection={categoryCollection}
                            name={field.name}
                            value={[field.value]}
                            onValueChange={(e) => { field.onChange(e.value?.[0] ?? null) }}
                            onInteractOutside={field.onBlur}
                            w="full"
                        >
                            <Select.HiddenSelect />

                            <Select.Control>
                                <Select.Trigger>
                                    <Select.ValueText placeholder="Kategorie" />
                                </Select.Trigger>

                                <Select.IndicatorGroup>
                                    <Select.Indicator />
                                </Select.IndicatorGroup>
                            </Select.Control>

                            <Portal>
                                <Select.Positioner>
                                    <Select.Content>
                                        {ct_read ? (
                                            categories_list?.data ? (
                                                categories_list?.data?.map(c => (
                                                    <Select.Item item={c.id} key={c.id}>
                                                        {c.name}
                                                        <Select.ItemIndicator />
                                                    </Select.Item>
                                                ))
                                            ) : (
                                                <Text>Keine Kategorien vorhanden</Text>
                                            )
                                        ) : (
                                            <Text color="danger">Keine Berechtigung für Kategorien</Text>
                                        )}
                                    </Select.Content>
                                </Select.Positioner>
                            </Portal>
                        </Select.Root>
                    )}
                />

                {errors?.navs?.[order_number]?.fk_category_id &&
                    <Field.ErrorText>{errors?.navs?.[order_number]?.fk_category_id.message}</Field.ErrorText>
                }
            </Field.Root>

            {/* Custom View */}
            <Field.Root mt={4}>
                <Field.Label htmlFor="custom_view">Layout</Field.Label>

                <Controller
                    control={control}
                    name={`navs.${order_number}.custom_view`}
                    render={({ field }) => (
                        <Select.Root
                            deselectable
                            multiple={false}
                            collection={customViewCollection}
                            name={field.name}
                            value={field.value ? [field.value] : []}
                            onValueChange={(e) => { field.onChange(e.value?.[0] ?? null) }}
                            onInteractOutside={field.onBlur}
                        >
                            <Select.HiddenSelect />

                            <Select.Control>
                                <Select.Trigger>
                                    <Select.ValueText placeholder="Layout" />
                                </Select.Trigger>

                                <Select.IndicatorGroup>
                                    <Select.Indicator />
                                </Select.IndicatorGroup>
                            </Select.Control>

                            <Select.Positioner>
                                <Select.Content>
                                    {customViewCollection ? (
                                        customViewCollection.items.map(c => (
                                            <Select.Item item={c.value} key={c.value}>
                                                {c.label}
                                                <Select.ItemIndicator />
                                            </Select.Item>
                                        ))
                                    ) : (
                                        <Text>Kein Layout vorhanden</Text>
                                    )}
                                </Select.Content>
                            </Select.Positioner>
                        </Select.Root>
                    )}
                />

                {errors?.navs?.[order_number]?.custom_view &&
                    <Field.ErrorText>{errors.navs?.[order_number]?.custom_view.message}</Field.ErrorText>
                }
            </Field.Root>

            <Field.Root mt={4}>
                <Field.Label htmlFor="file">Kategoriebild</Field.Label>

                {me_create ? (
                    me_read ? (
                        <Controller
                            control={control}
                            name={`navs.${order_number}.file`}
                            render={({ field }) => (
                                <FileUpload.Root alignItems="stretch" maxFiles={1} accept="image/*">
                                    <FileUpload.HiddenInput
                                        data-testid="nav_image"
                                        onChange={(e) => {
                                            field.onChange(e.target.files?.item(0))
                                        }}
                                    />

                                    <FileDropzone
                                        hidden={true}
                                        file={field.value}
                                        path={getValues(`navs.${order_number}.file_path`) ?? null}
                                        onClick={() => {
                                            field.onChange(null)
                                            setValue(`navs.${order_number}.file_path`, null, { shouldDirty: true })
                                        }}
                                    />

                                    <FileUpload.Trigger asChild>
                                        <Button variant="secondary" size="sm">
                                            <Icon as={FiUpload} size="sm" /> Kategoriebild hochladen
                                        </Button>
                                    </FileUpload.Trigger>
                                </FileUpload.Root>
                            )}
                        />
                    ) : (
                        <Text color="danger">Keine Berechtigung um Bilder anzusehen</Text>
                    )
                ) : (
                    <Text color="danger">Keine Berechtigung um Bilder zu erstellen</Text>
                )}
            </Field.Root>

            {children}
        </>
    )
}

export default SettingsNavPoint